import { Coordinate, CoordinateBounds } from "../hooks/use-coordinates";
import { LngLat } from "mapbox-gl";
import {
  LineStyle,
  PointStyle,
  LabelDecoration,
  Point,
  Size,
  Shadow,
} from "./poster-types";
import { Thumbnail as MapStyleThumbnail } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-thumbnail-grid";
import {
  EditingPermissions,
  LabelEditingPermissions,
  BackdropEditingPermissions,
  LineEditingPermissions,
  BorderEditingPermissions,
  PointEditingPermissions,
} from "./editing-permissions";
import { Orientation } from "./size-presets/size-preset";

export const PosterProjectCurrentModelVersion = 11;

export interface ProjectStore {
  projects: PosterProject[];
}

export type GPSData = {
  elevationGain: number;
  start: Date | undefined;
  end: Date | undefined;
  distanceMeters: number;
};

export interface GPSSource {
  id: string;
  name?: string;
  originDescription: string; //File name or other source.
  allCoordinates: Coordinate[];
  displayedCoordinates: Coordinate[];
  coordinateLimit?: number;
  data: GPSData | undefined;
}

export interface PosterProject {
  id: string;
  name: string;
  modelVersion: number;
  thumbnail?: string;
  createdAt: Date;
  updatedAt: Date;

  //ID of the last rendered canvas, to prevent caching (if using the same name like
  //"canvas".  Tracked so we can delete the old one when we make a new map render.
  lastRenderID: string | undefined;
  isRendered: boolean; //Indicates if the project has been rendered or not.

  //Global editing permissions for the project.
  //Each element such as map, labels, etc. can have its own editing permissions.
  //They are populated based on the template the project was generated from.
  editingPermissions: EditingPermissions;

  //Indicates whether the project has just been created from a template and the user
  //hasn't yet decided to edit/commit to it and save it as a new project.
  //If this is true, can be considered discardable.
  isTemplateDraft: boolean;

  isInCheckout: boolean; //Indicates if the project is in the checkout process.
  isOrdered: boolean; //Indicates if the project has been purchased through cart or not.

  gpsSourceIDs: string[]; //IDs of the GPS sources used in this project.
  gpsData: { id: string; data: GPSData } | undefined;

  // STYLES APPLIED TO ADDED ELEMENTS BY DEFAULT/PRESET.
  // Individual elements have their own styles, taken from these presets
  // initially and possibly modified by the user.
  legendStyle: PosterLegendStyle;
  pointStyle: PointStyle;
  lineStyle: LineStyle;
  borderStyle: BorderStyle;
  backdropStyle: BackdropStyle;

  mapLabelStyle: MapLabelStyle;
  staticLabelStyle: StaticLabelStyle;

  sizeOption: ProjectSize;

  size: PosterSizePreset; // Deprecated.

  mapStyle: string; // Actual map style from map box.

  bounds?: CoordinateBounds;

  legend: PosterLegend;

  styledPoints: StyledPoint[];
  styleUpdateKey?: string; // Used to force a rerender when styles change.

  mapLabels: MapLabel[];
  staticLabels: StaticLabel[];
  labelUpdateKey?: string; // Used to force a rerender of the labels.

  backdrops: Backdrop[];
  backdropUpdateKey?: string; // Used to force a rerender of the backdrops.
}

export interface BorderStyle {
  borderWidth?: number; // In MM.
  borderColor?: string;
}

export interface ProjectSize {
  presetID: string | undefined;
  options: {
    widthPX?: number | undefined; // Only if preset is custom px by px.
    heightPX?: number | undefined; // Only if preset is custom px by px.
    orientation: Orientation;
  };
}

export type PosterLegendPosition = "bottom" | "top" | "left" | "right";

export interface PosterLegend {
  labels: string[];
  style: PosterLegendStyle;
}

export interface PosterLegendStyle {
  visible: boolean;
  labelColor: string;
  backgroundColor: string;
  backgroundOpacity: number;
  position: PosterLegendPosition;
  labelTemplate: PosterLegendLableTemplate;
}

// These define where the labels go, their font size, and their color, not the legend's backgorund
// I've created a component for each of these layouts, which combined with `orientation`
// will determine the final layout of the legend.
// E.g. layout_03 might have classic styled labels for title, two subtitles, and a third label at the bottom
// with a sans serif font (positioned differently for landscape and portrait).
export type PosterLegendLableTemplate = "layout_01" | "layout_02" | "layout_03";

// Determines how a label grows as text is entered.
// e.g. if you align a lable to the right side of the poster, you want it to grow/shrink
// to the left. Otherwise presets would get messed up when you start entering your own text.
export type TextAlignment = "left" | "center" | "right";

export type BackdropType = "color" | "image" | "dataVisualization";
export type DataVisualization = "none" | "elevation" | "speed";

export interface StyledPoint {
  id: string;
  coordinate: Coordinate;
  style: PointStyle;
  editingPermissions?: PointEditingPermissions;
  label?: MapLabel | undefined;
}

export interface Backdrop {
  id: string;
  style: BackdropStyle;
  position: Point;

  name: string;
  //For backdrops, size.width is RATIO the width.
  //This is so they scale with map width (which affects font sizes).
  size: Size;

  editingPermissions?: BackdropEditingPermissions;

  type: BackdropType;

  dataVisualation?: DataVisualization;
  imageURL?: string;
}

export interface BackdropStyle {
  backgroundColor: string;
  foregroundColor: string;
  opacity: number; //Background opacity.
  foregroundOpacity: number;
  borderWidth: number;
  borderColor: string;
  cornerRadius: number;
  imageName?: string;
  padding: number; // Padding around the backdrop for data visualizations.
}

export type MapLabelType = "text" | "image";
export interface MapLabel {
  id: string;
  pointID: string;
  coordinate: Coordinate;
  text: string;
  style: MapLabelStyle;
  editingPermissions?: LabelEditingPermissions;
  type: MapLabelType; //Deprecated
}

export interface MapLabelStyle {
  showImage: boolean;
  showText: boolean;
  backgroundColor: string;
  backgroundOpacity: number;
  padding?: number;
  decorationStyle: MapLabelDecorationStyle;
  showPointer: boolean;
  pointerColor: string;
  pointerThickness: number;
  font: string;
  textColor: string;
  fontSize: number;
  offset: { x: number; y: number };
  alignment: TextAlignment;
  lineSpacing: number;

  isUppercase: boolean;

  imageName?: string;

  // For "image" type labels.
  borderRadius: number;
  borderWidth: number;
  borderColor: string;
  imageSize: number;

  shadow: Shadow | undefined;
}

export interface StaticLabel {
  id: string;
  description: string;
  text: string;
  position: Point;
  style: StaticLabelStyle;
  editingPermissions?: LabelEditingPermissions;
  isEdited?: boolean;
}

export interface StaticLabelStyle {
  alignment: TextAlignment;
  lineSpacing: number;
  backgroundColor: string;
  backgroundOpacity: number;
  padding: number;
  borderRadius: number;
  font: string;
  textColor: string;
  fontSize: number;
  isUppercase: boolean;
  isHidden: boolean;
}

export interface MapLabelDecorationStyle {
  decoration: LabelDecoration;
  width: number; //% not px.
  length: number; //% not px.
  color: string;
}

export interface PhotoWithCoordinates {
  url: string;
  coordinates: Coordinate;
}

export const defaultCoordinateLimit = 5000;

export type UnitMeasurement = "inches" | "cm";
export const AvailableUnitMeasurements: UnitMeasurement[] = ["inches", "cm"];

export type PosterSizePreset =
  | "6x6"
  | "8x8"
  | "A4 Portrait"
  | "A4 Landscape"
  | "12x12"
  | "A3 Portrait"
  | "A3 Landscape"
  | "16x16";

export const AvailableSizePresets: PosterSizePreset[] = [
  // "12x20",
  "6x6",
  "8x8",
  "A4 Portrait",
  "A4 Landscape",
  "12x12",
  "A3 Portrait",
  "A3 Landscape",
  "16x16",
];

// export const cssAspectRatioFromPreset = (preset: PosterSizePreset) => {
//   switch (preset) {
//     case "6x6":
//       return "6 / 6";
//     case "8x8":
//       return "8 / 8";
//     case "12x12":
//       return "12 / 12";
//     case "A4 Portrait":
//       return "0.7242";
//     case "A4 Landscape":
//       return "1 / 0.7242";
//     case "A3 Portrait":
//       return "0.7499";
//     case "A3 Landscape":
//       return "1 / 0.7499";
//     case "16x16":
//       return "16 / 16";
//     default:
//       return "12 / 12";
//   }
// };

export const sizeInInchesFromPreset = (
  preset: PosterSizePreset
): { width: number; height: number } => {
  switch (preset) {
    case "6x6":
      return { width: 6, height: 6 };
    case "8x8":
      return { width: 8, height: 8 };
    case "12x12":
      return { width: 12, height: 12 };
    case "A4 Portrait":
      return { width: 8.3, height: 11.454 };
    case "A4 Landscape":
      return { width: 11.454, height: 8.3 };
    case "A3 Portrait":
      return { width: 11.7, height: 15.596 };
    case "A3 Landscape":
      return { width: 15.596, height: 11.7 };
    case "16x16":
      return { width: 16, height: 16 };
  }
  return { width: 30, height: 30 };
};

export const sizeInCMFromPreset = (
  preset: PosterSizePreset
): { width: number; height: number } => {
  switch (preset) {
    case "6x6":
      return { width: 15, height: 15 };
    case "8x8":
      return { width: 20, height: 20 };
    case "12x12":
      return { width: 30, height: 30 };
    case "A4 Portrait":
      return { width: 21, height: 28.98 };
    case "A4 Landscape":
      return { width: 28.98, height: 21 };
    case "A3 Portrait":
      return { width: 29.7, height: 39.723 };
    case "A3 Landscape":
      return { width: 39.723, height: 29.7 };
    case "16x16":
      return { width: 40, height: 40 };
  }
  return { width: 30, height: 30 };
};

export const aspectRatioFromPreset = (preset: PosterSizePreset) => {
  let sizeInCM = sizeInCMFromPreset(preset);
  if (sizeInCM === undefined) {
    sizeInCM = { width: 30, height: 30 };
  }
  return sizeInCM.width / sizeInCM.height;
};

export const screenSizeFromPreset = (
  preset: PosterSizePreset
): { width: number; height: number } => {
  const aspectRatio = aspectRatioFromPreset(preset);
  const maxHeight = 700;
  const maxWidth = 1200;

  if (aspectRatio > 1) {
    return { width: maxWidth, height: maxWidth / aspectRatio };
  } else {
    return { width: maxHeight * aspectRatio, height: maxHeight };
  }
};

export interface MapStaticImageOptions {
  northeast: LngLat;
  southwest: LngLat;
  project: PosterProject;
  isThumbnail: boolean;
}

export interface MapStyle {
  mapBoxStyleID: string;
  styleURL: string;
  name: string;
}

export const MapStyleBaseURL = "mapbox://styles/jamesbkelly/";

export const generateMapStyles = async () => {
  const style = await mapStyleFromManifest();
  const mapStyles: MapStyle[] = [];
  style.styles.forEach((style: any) => {
    let id = style.styleId;
    mapStyles.push({
      mapBoxStyleID: id,
      styleURL: `${MapStyleBaseURL}${id}`,
      name: style.name,
    });
  });
  return mapStyles;
};

const mapStyleFromManifest = async () => {
  const path = "/pallete-components/map-thumbnails/map-style-manifest.json";
  let response = await fetch(path, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });
  let data = await response.json();
  return data;
};

export const pickerColors = [
  "#FFFFFF",
  "#F7F7F7",
  "#DCDCDC",
  "#6B6B6B",
  "#2A2A2A",
  "#000000",
  "#ECADC4",
  "#F9357C",
  "#EE2525",
  "#E71B55",
  "#B20536",
  "#68001D",
  "#FEB237",
  "#FEDD37",
  "#EDFE37",
  "#DFF8E3",
  "#EEFAE3",
  "#6CD888",
  "#22BA52",
  "#236A39",
  "#03290F",
  "#ADECE4",
  "#DAF7F3",
  "#FF4FBB",
  "#ED1EDB",
  "#D172F7",
  "#005AB4",
  "#4E13C3",
  "#014181",
  "#5910EB",
  "#002E5C",
  "#6E2CF1",
];

export const fontNames = (): string[] => {
  return [
    "AlfaSlabOne",
    "Anton",
    "Archivo",
    "BebasNeue",
    "BigShouldersInlineText",
    "BlackOpsOne",
    "BungeeHairline",
    "BungeeShade",
    "Dosis",
    "FaunaOne",
    "FrancoisOne",
    "FrankRuhlLibre",
    "FrederickatheGreat",
    "Fredoka",
    "FugazOne",
    "GasoekOne",
    "GolosText",
    "GravitasOne",
    "Gugi",
    "JacquesFrancoisShadow",
    "Kanit",
    "Karla",
    "KodeMono",
    "LeagueGothic",
    "LexendTera",
    "LibreBarcode39Text",
    "LibreBaskerville",
    "LondrinaShadow",
    "MPLUS1Code",
    "MajorMonoDisplay",
    "MoiraiOne",
    "Monoton",
    "NotoSansKR",
    "NotoSerifHentaigana",
    "NovaMono",
    "Nunito",
    "Oswald",
    "PTMono",
    "Pacifico",
    "PaytoneOne",
    "PlayfairDisplay",
    "PlaywriteAUSA",
    "PlaywriteITModerna",
    "PressStart2P",
    "ProtestGuerrilla",
    "Quicksand",
    "Raleway",
    "RalewayDots",
    "ReenieBeanie",
    "Rock3D",
    "RockSalt",
    "Rowdies",
    "RubikDirt",
    "RubikDoodleShadow",
    "RubikMaps",
    "RubikMicrobe",
    "ScheherazadeNew",
    "ShadowsIntoLight",
    "ShareTech",
    "Shizuru",
    "Slackey",
    "SmoochSans",
    "SpecialElite",
    "Teko",
    "TextMeOne",
    "Tourney",
    "VT323",
    "Zeyada",
  ];
};

export const prodigiProductRecommendationForSize = (
  preset: PosterSizePreset
): string => {
  switch (preset) {
    case "A4 Portrait":
      return '9x12"';
    case "A4 Landscape":
      return '9x12"';
    case "A3 Portrait":
      return '12x16"';
    case "A3 Landscape":
      return '12x16"';
    case "6x6":
      return '6x6"';
    case "8x8":
      return '8x8"';
    case "12x12":
      return '12x12"';
    case "16x16":
      return '16x16"';
  }
  return "-";
};
